import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { toast } from 'sonner';

import { serialiseCosts } from './cost-selection';
import { sectionLeadType, useMarketingLeadType } from './lead-type';
import * as marketingApi from './marketing-reports.api';
import { serialiseMonths } from './month-selection';

import type {
  CreateMarketingCostHeadRequest,
  DeleteMarketingCostEntryRequest,
  SetManagementCostRequest,
  UpdateMarketingCostHeadRequest,
  UpsertMarketingCostEntryRequest,
} from '@utazzo/shared';

import { extractServerError } from '@/lib/api-error';

/**
 * Every read is keyed by the selected month SET (not a from/to pair), because
 * the picker allows holes - May+July and May+June+July are different reports
 * and must not share a cache entry. The two cost-bearing reads (spend, profit)
 * are additionally keyed by the "include costs" pick, for the same reason.
 *
 * Pass `null` months to hold a query back (tab not opened yet).
 */
const key = (
  part: string,
  months: string[] | null,
  costs: string[] | null = null,
  leadType: string = '',
) =>
  [
    'marketing-reports',
    part,
    months ? serialiseMonths(months) : '',
    costs === null ? 'all' : serialiseCosts(costs),
    leadType,
  ] as const;

/**
 * The signed-in user's hidden Marketing Reports fields. `isHidden(key)` lets any
 * tile/column skip rendering a field the admin hid from this user (the API has
 * also nulled it). Cached long - visibility changes rarely within a session.
 */
export const useMarketingFieldVisibility = (): {
  hidden: Set<string>;
  isHidden: (key: string) => boolean;
  isLoading: boolean;
} => {
  const q = useQuery({
    queryKey: ['marketing-reports', 'field-visibility'] as const,
    queryFn: marketingApi.getFieldVisibility,
    staleTime: 300_000,
  });
  const hidden = useMemo(() => new Set(q.data ?? []), [q.data]);
  return { hidden, isHidden: (fieldKey: string) => hidden.has(fieldKey), isLoading: q.isLoading };
};

/** Shared across every read here - the report is not a live feed. */
const READ_OPTIONS = { staleTime: 30_000, refetchOnWindowFocus: false } as const;

/** Funnel standing of the range's queries. Pass null until the tab is opened. */
export const useMarketingLeadStatus = (months: string[] | null) => {
  const leadType = useMarketingLeadType();
  return useQuery({
    queryKey: key('lead-status', months, null, leadType),
    queryFn: () => marketingApi.getMarketingLeadStatus(months!, sectionLeadType(leadType)),
    enabled: Boolean(months?.length),
    ...READ_OPTIONS,
  });
};

/** Per-source breakdown. Pass null until the tab is opened. */
export const useMarketingChannels = (months: string[] | null) => {
  const leadType = useMarketingLeadType();
  return useQuery({
    queryKey: key('channels', months, null, leadType),
    queryFn: () => marketingApi.getMarketingChannels(months!, sectionLeadType(leadType)),
    enabled: Boolean(months?.length),
    ...READ_OPTIONS,
  });
};

/**
 * The profit table. Pass null until the cost gate is satisfied so the heavier
 * lead/itinerary aggregate only fires once the report is unlocked.
 */
export const useMarketingQueryProfit = (months: string[] | null, costs: string[] | null = null) => {
  const leadType = useMarketingLeadType();
  return useQuery({
    queryKey: key('query-profit', months, costs, leadType),
    queryFn: () => marketingApi.getMarketingQueryProfit(months!, costs, sectionLeadType(leadType)),
    enabled: Boolean(months?.length),
    ...READ_OPTIONS,
  });
};

export const useMarketingSpend = (months: string[] | null, costs: string[] | null = null) =>
  useQuery({
    queryKey: key('spend', months, costs),
    queryFn: () => marketingApi.getMarketingSpend(months!, costs),
    enabled: Boolean(months?.length),
    ...READ_OPTIONS,
  });

/* ------------------------------ Cost heads master ------------------------------ */

/** Every live head, active or not - the master list and the filter's options. */
/**
 * `enabled: false` for a caller who may not read costs (the Organic section) -
 * the API refuses the list to them, so it is not asked for.
 */
export const useMarketingCostHeads = (enabled = true) =>
  useQuery({
    queryKey: ['marketing-reports', 'cost-heads'] as const,
    queryFn: () => marketingApi.getMarketingCostHeads(),
    enabled,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

/** A head change moves every month's cost side, so the whole report refetches. */
const invalidateReport = (queryClient: ReturnType<typeof useQueryClient>): void => {
  void queryClient.invalidateQueries({ queryKey: ['marketing-reports'] });
};

export const useCreateCostHead = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateMarketingCostHeadRequest) =>
      marketingApi.createMarketingCostHead(body),
    onSuccess: (head) => {
      invalidateReport(queryClient);
      toast.success(`Added ${head.name}`);
    },
    onError: (err: unknown) =>
      toast.error(extractServerError(err).message ?? 'Failed to add cost head'),
  });
};

export const useUpdateCostHead = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateMarketingCostHeadRequest }) =>
      marketingApi.updateMarketingCostHead(id, body),
    onSuccess: () => {
      invalidateReport(queryClient);
      toast.success('Cost head updated');
    },
    onError: (err: unknown) =>
      toast.error(extractServerError(err).message ?? 'Failed to update cost head'),
  });
};

export const useDeleteCostHead = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => marketingApi.deleteMarketingCostHead(id),
    onSuccess: () => {
      invalidateReport(queryClient);
      toast.success('Cost head deleted');
    },
    onError: (err: unknown) =>
      toast.error(extractServerError(err).message ?? 'Failed to delete cost head'),
  });
};

/** Save one month's figure for a head - the inline cost cells on the sheet. */
export const useUpsertCostEntry = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: UpsertMarketingCostEntryRequest) =>
      marketingApi.upsertMarketingCostEntry(body),
    onSuccess: () => invalidateReport(queryClient),
    onError: (err: unknown) =>
      toast.error(extractServerError(err).message ?? 'Could not save the cost'),
  });
};

export const useDeleteCostEntry = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: DeleteMarketingCostEntryRequest) =>
      marketingApi.deleteMarketingCostEntry(body),
    onSuccess: () => invalidateReport(queryClient),
    onError: (err: unknown) =>
      toast.error(extractServerError(err).message ?? 'Could not clear the cost'),
  });
};

/** Every cost write feeds the same gate, so each invalidates the whole report. */
const invalidateSpend = (queryClient: ReturnType<typeof useQueryClient>): void => {
  void queryClient.invalidateQueries({ queryKey: ['marketing-reports'] });
};

export const useSetManagementCost = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: SetManagementCostRequest) => marketingApi.setManagementCost(body),
    onSuccess: () => invalidateSpend(queryClient),
  });
};

/** Region- and agent-wise contactability. Pass null until the tab is opened. */
export const useMarketingContactability = (months: string[] | null) => {
  const leadType = useMarketingLeadType();
  return useQuery({
    queryKey: key('contactability', months, null, leadType),
    queryFn: () => marketingApi.getMarketingContactability(months!, sectionLeadType(leadType)),
    enabled: Boolean(months?.length),
    ...READ_OPTIONS,
  });
};

/** Campaign-wise contactability, its own report. Pass null until the tab is opened. */
export const useMarketingCampaignContactability = (months: string[] | null) => {
  const leadType = useMarketingLeadType();
  return useQuery({
    queryKey: key('campaign-contactability', months, null, leadType),
    queryFn: () =>
      marketingApi.getMarketingCampaignContactability(months!, sectionLeadType(leadType)),
    enabled: Boolean(months?.length),
    ...READ_OPTIONS,
  });
};

/** Month-wise active status by channel. Pass null until the tab is opened. */
export const useMarketingActiveStatus = (months: string[] | null) => {
  const leadType = useMarketingLeadType();
  return useQuery({
    queryKey: key('active-status', months, null, leadType),
    queryFn: () => marketingApi.getMarketingActiveStatus(months!, sectionLeadType(leadType)),
    enabled: Boolean(months?.length),
    ...READ_OPTIONS,
  });
};

/** Destination performance. Pass null until the tab is opened. */
/** Today's daily lead caps. Not keyed by month or section: it describes today. */
export const useMarketingDailyCaps = () =>
  useQuery({
    queryKey: ['marketing-reports', 'daily-caps'] as const,
    queryFn: marketingApi.getMarketingDailyCaps,
    ...READ_OPTIONS,
  });

export const useMarketingDestinations = (months: string[] | null) => {
  const leadType = useMarketingLeadType();
  return useQuery({
    queryKey: key('destinations', months, null, leadType),
    queryFn: () => marketingApi.getMarketingDestinations(months!, sectionLeadType(leadType)),
    enabled: Boolean(months?.length),
    ...READ_OPTIONS,
  });
};
